import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { endOfWeek } from 'date-fns';
import { API_BASE_URL } from '../config/api';

export const useCalendarFetch = ({
  teamspaces,
  weekStart,
  timezone,
  selectedMember, // mandatory: the calendar shows one member's week
  timezoneReady,
  refreshTrigger = 0,
  wsRefreshTrigger = 0,
  backgroundRefreshRef = null,  // Ref: set .current=true before wsUpdateTrigger to skip loading
}) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const abortControllerRef = useRef(null);
  const prevDepsRef = useRef(null);

  /**
   * One member's week per teamspace, with `include_groups=true`: every grouped entry
   * carries its whole group (other members' entries included) in `group`, so the
   * calendar no longer fetches everyone's week to detect groups (2026-09-22). The old
   * everyone-fetch read only the first 200 rows and missed groups on the early days
   * of busy weeks. Reads every page in case a member ever has more than one
   * (peak so far: 113 entries), de-duplicating by id: rows sharing a start_time can
   * shift between pages.
   */
  const fetchFromApi = useCallback(async (start, end, memberUsername, signal) => {
    if (!teamspaces?.length || !memberUsername) return [];

    const tag = (e, ts) => ({ ...e, teamspace_id: ts.id, teamspace_name: ts.name });
    const promises = teamspaces.map(async (ts) => {
      try {
        const base = `${API_BASE_URL}teamspaces/${ts.id}/time_entries/?start_date=${start}&end_date=${end}`
          + `&timezone=${timezone}&page_size=200&is_optimized=true&include_groups=true`
          + `&member_username=${encodeURIComponent(memberUsername)}`;
        const byId = new Map();
        // Page by number rather than following `next`: DRF builds that link from the
        // request host, which behind the proxy can come back http:// on an https:// site.
        for (let page = 1; ; page += 1) {
          const res = await axios.get(`${base}&page=${page}`, { signal });
          const results = Array.isArray(res.data) ? res.data : (res.data.results || []);
          for (const e of results) {
            byId.set(String(e.id), {
              ...tag(e, ts),
              ...(e.group ? { group: e.group.map(g => tag(g, ts)) } : {}),
            });
          }
          if (Array.isArray(res.data) || !res.data.next) break;
        }
        return [...byId.values()];
      } catch (err) {
        return [];
      }
    });

    const results = await Promise.all(promises);
    return results.flat();
  }, [teamspaces, timezone]);

  useEffect(() => {
    // Guard: Don't fetch if basics are missing
    if (!timezoneReady || !teamspaces?.length || !weekStart) return;
    if (!selectedMember) return;

    // Primary deps = user navigation (week, member, timezone). Triggers (refreshTrigger, wsRefreshTrigger) are separate.
    const primaryKey = [
      weekStart?.getTime?.(),
      selectedMember,
      timezone,
      teamspaces?.length
    ].join('|');
    const prev = prevDepsRef.current;
    const onlyWsTriggerChanged = prev !== null &&
      prev.primaryKey === primaryKey &&
      prev.wsRefreshTrigger !== wsRefreshTrigger;
    prevDepsRef.current = { primaryKey, wsRefreshTrigger };

    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const isBackgroundRefresh = backgroundRefreshRef?.current === true;
    if (isBackgroundRefresh && backgroundRefreshRef) {
      backgroundRefreshRef.current = false;
    }

    const fetchData = async () => {
      if (!isBackgroundRefresh) {
        setLoading(true);
      }

      try {
        // --- RESTORE THE 1-DAY PADDING ---
        // This ensures UTC-converted entries from the previous/next day are caught
        const paddedStart = new Date(weekStart);
        paddedStart.setDate(paddedStart.getDate() - 1);
        // From the previous day's midnight, not its noon (weekStart is pinned to noon):
        // the phone's 3-day view can show that whole Sunday (Sun–Tue anchors on Monday).
        paddedStart.setHours(0, 0, 0, 0);
        
        const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
        const paddedEnd = new Date(weekEnd);
        paddedEnd.setDate(paddedEnd.getDate() + 1);
        
        const startISO = paddedStart.toISOString();
        const endISO = paddedEnd.toISOString();

        console.log(`🚀 Fetching for: ${selectedMember} | Range: ${startISO} - ${endISO}`);
        const data = await fetchFromApi(startISO, endISO, selectedMember, controller.signal);
        
        if (!controller.signal.aborted) {
          setEntries(data);
          setLoading(false);
        }
      } catch (err) {
        if (err.name !== 'CanceledError') setLoading(false);
      }
    };

    fetchData();
    return () => controller.abort();
  }, [weekStart, teamspaces, timezone, timezoneReady, selectedMember, fetchFromApi, refreshTrigger, wsRefreshTrigger]);

  return { entries, setEntries, loading };
};